import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  StatusReportTemplateKey,
  type StatusReportTemplate,
} from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useStatusReportTemplatesStore } from "@/store/statusReportTemplatesStore";
import { TEMPLATE_DISPLAY } from "./templateDisplay";
import { StandardSettingsForm } from "./StandardSettingsForm";
import { SalesRepSettingsForm } from "./SalesRepSettingsForm";
import { EstimatorSettingsForm } from "./EstimatorSettingsForm";
import { ManagerSettingsForm } from "./ManagerSettingsForm";

interface TemplateSettingsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: StatusReportTemplate | null;
}

/**
 * Edit-in-sheet for one status-report template's settings and org blurb. The
 * persisted bag is parsed to a fully-defaulted form on open, edited locally,
 * and saved whole; the server re-validates against the same shared schema.
 */
export function TemplateSettingsSheet({
  open,
  onOpenChange,
  template,
}: TemplateSettingsSheetProps) {
  const loadingByKey = useStatusReportTemplatesStore(
    (state) => state.loadingByKey,
  );
  const saveTemplate = useStatusReportTemplatesStore(
    (state) => state.saveTemplate,
  );

  const [draft, setDraft] = useState<StatusReportTemplate | null>(null);
  const [blurb, setBlurb] = useState("");
  const isSaving = template
    ? (loadingByKey[template.templateKey]?.isLoadingSave ?? false)
    : false;

  useEffect(() => {
    if (open && template) {
      setDraft(template);
      setBlurb(template.reportContextBlurb ?? "");
    }
  }, [open, template]);

  const handleSave = async () => {
    if (!template || !draft) {
      return;
    }
    const result = await saveTemplate(draft, blurb.trim() ? blurb : null);
    if (result) {
      toast.success("Report settings saved");
      onOpenChange(false);
      return;
    }
    toast.error(
      useStatusReportTemplatesStore.getState().loadingByKey[
        template.templateKey
      ]?.saveError ?? "Failed to save report settings",
    );
  };

  const display = template ? TEMPLATE_DISPLAY[template.templateKey] : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[600px] md:max-w-[700px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{display?.title ?? "Report settings"}</SheetTitle>
          <SheetDescription>
            Tune what qualifies, how it ranks, and how the narrative reads.
            Layout and sections never change.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 py-6">
          {draft && <DraftForm draft={draft} onChange={setDraft} />}

          <div className="space-y-1.5">
            <Label className="text-sm">Organization context blurb</Label>
            <p className="text-xs text-muted-foreground">
              Optional context the narrative writer reads (never restated
              verbatim). Shared across this template&apos;s sections.
            </p>
            <Textarea
              rows={3}
              value={blurb}
              onChange={(e) => setBlurb(e.target.value)}
              placeholder="e.g. We fabricate structural steel; lead times matter more than price."
            />
          </div>
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !draft}>
            Save
            {isSaving && (
              <Loader2 className="ml-1.5 h-3.5 w-3.5 animate-spin" />
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Renders the matching per-template form for the draft. The draft is the
 * discriminated template row, so switching on its key narrows both the settings
 * it holds and the settings the form hands back.
 */
function DraftForm({
  draft,
  onChange,
}: {
  draft: StatusReportTemplate;
  onChange: (draft: StatusReportTemplate) => void;
}) {
  switch (draft.templateKey) {
    case StatusReportTemplateKey.STANDARD:
      return (
        <StandardSettingsForm
          value={draft.settings}
          onChange={(settings) => onChange({ ...draft, settings })}
        />
      );
    case StatusReportTemplateKey.SALES_REP:
      return (
        <SalesRepSettingsForm
          value={draft.settings}
          onChange={(settings) => onChange({ ...draft, settings })}
        />
      );
    case StatusReportTemplateKey.ESTIMATOR:
      return (
        <EstimatorSettingsForm
          value={draft.settings}
          onChange={(settings) => onChange({ ...draft, settings })}
        />
      );
    case StatusReportTemplateKey.MANAGER:
      return (
        <ManagerSettingsForm
          value={draft.settings}
          onChange={(settings) => onChange({ ...draft, settings })}
        />
      );
  }
}
